const content = {
  component: "PageTypeA",
  cssClass: "page-two",
  meta: {
    title: "Guaranteed Origins, Guaranteed Quality",
    description: `Blockchain is transforming the olive oil supply chain, ensuring customers can trust product authenticity.`,
    og: {
      type: "website",
      title: "Blockchain ensures authenticity—read how",
      description: `In a world where customer trust is decreasing, it’s vital olive oil retailers can prove their products' country of origin. By utilising blockchain, the supply chain is made fully transparent and traceable, ensuring all parties can be confident of where a product has come from.  `,
      image: require("../../assets/images/social/guaranteed-origins-1200x630.jpg")
    },
    twitter: {
      card: "summary",
      title:
        "Learn how blockchain is revolutionizing the olive oil supply chain",
      description: `50-90% of virgin olive oil is mislabelled. In a world of diminishing consumer trust, the transparency, security and scalability of blockchain ensures retailers can guarantee their products' origin.`,
      site: "@Oracle",
      creator: "@Oracle",
      image: require("../../assets/images/social/guaranteed-origins-300x300.jpg")
    }
  },
  title: "<span>Guaranteed Origins, </span><span>Guaranteed Quality</span>",
  heroImage: {
    preload: require("../../assets/images/bgs/guaranteed-origins-pre.jpg"),
    loaded: require("../../assets/images/bgs/guaranteed-origins.jpg"),
    mobile: require("../../assets/images/bgs/guaranteed-origins-mobile.jpg")
  },
  // * the heroImageOptions are currently only required and supported when using the PageTypeA component
  heroImageOptions: {
    default: {
      customPosition: [false]
    },
    mobile: {
      customPosition: [false]
    },
    limtedHeight: {
      customPosition: [false]
    }
  },
  // floodlight: 'https://3573292.fls.doubleclick.net/activityi;src=3573292;type=oracl0;cat=fy19q0;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;ord=1;',
  sections: [
    {
      component: "SectionCopy",
      content: {
        copy: `
          <h2>The Authentic Oil Experience</h2>
          <p>Picture yourself relaxing in the late afternoon Italian sun, sipping from a glass of Chianti.  Laid before you on a rustic wooden table is a sumptuous spread including parmesan dusted roasted vegetables, crisply grilled Panini, and glistening slices of deep red and orange tomato.</p>
        `,
        type: "intro"
      }
    },
    {
      component: "SectionSeperator",
      content: {
        image: null
      }
    },
    {
      component: "SectionCopy",
      content: {
        copy: `
          <p>Choosing your first bite is difficult—that is, until you spot a small bowl of golden liquid among the platters. You dip a piece of crusty bread and eat, savouring the peppery taste and fruity aromas of authentic extra virgin olive oil.</p>
          <p>This is the kind of experience Certified Origins and Bellucci want customers around the globe to recreate every time they sauté, drizzle, or dip.  Indeed, customers are increasingly concerned with provenance and authenticity.  Almost 60% of UK consumers list country of origin at least as important as product benefits or price. <sup>1</sup></p>
        `,
        type: "default"
      }
    },
    {
      component: "SectionSeperator",
      content: {
        image: require("../../assets/images/svgs/oil.svg")
      }
    },
    {
      component: "SectionCopy",
      content: {
        copy: `
          <p>Currently, however, because of a few industry bad actors and the limits of third-party regulation, customer trust is under threat. Reports estimate between 50-90% of extra-virgin olive oil sold globally is adulterated and/or mislabelled. <sup>2</sup></p>
          <p>This unfortunate reality is why Certified Origins has always had a passionate commitment to guaranteed sourcing and quality assurance. “Trust Through Traceability is the core philosophy of the Bellucci brand,’’ explains Susan Testa, Director of Culinary Innovation.</p>
        `,
        type: "default"
      }
    },
    {
      component: "SectionQuote",
      content: {
        quote: `We work closely with thousands of small family farmers, each one tending  olive trees in the traditional Italian way and supplying highest quality varietals for Bellucci extra virgin olive oil and other blends.`,
        author: `Susan Testa`,
        position: `Director of Culinary Innovation, Certified Origins`,
        type: "quote",
        backgroundImage: require("../../assets/images/bgs/GuaranteedOrigins_CertifiedOrigins_bg.jpg")
      }
    },
    {
      component: "SectionCopy",
      content: {
        copy: `
          <h3>Blockchain and the Traceability Upgrade</h3>
          <p>Certified Origins has always led industry innovation, so it’s natural they are among the first to use blockchain to track supply chain transactions.  In their case, blockchain reinforces and proves an already robust and multi-layered regulation and certification program.</p>
          <p>Certified Origins uses blockchain technology to track the olive oil journey from manufacture to delivery.  This kind of tracking functions as a secure electronic ledger, a ‘chain’ of information ‘blocks’. The blockchain is a tamper-proof, permanent and public information trail.</p>
        `,
        type: "default"
      }
    },
    {
      component: "SectionImage",
      content: {
        image: require("../../assets/images/content/GuaranteedOrigins_InlineImage.jpg"),
        alt: "",
        type: "image"
      }
    },
    {
      component: "SectionCopy",
      content: {
        copy: `
          <h3>How Blockchain Works for Food Transparency</h3>
          <p>Some still equate blockchain with cryptocurrency, but the technology use cases extend far beyond Bitcoin and Ethereum. In fact, experts believe blockchain is poised to have a bigger impact than the internet on the way companies do business. <sup>3</sup></p>
          <p>Information within the blockchain is only accepted by consensus, is completely secure, and easily auditable. For food suppliers like Certified Origins, blockchain provides business value in three significant ways:</p>
        `,
        type: "default"
      }
    },
    {
      component: "SectionCopy",
      content: {
        copy: `
          <p><b>Enhanced Transparency:</b> Certified Origins can document the origin and touchpoints for olives and olive oil.  These logs can also provide additional evidence for regulators and serve certification requirements.</p>
          <p><b>Increased Security</b>: A shared, unalterable ledger can reduce or eliminate the need for time and resource-intensive audits.</p>
          <p><b>Infinite Scalability</b>: All members of the co-operative working with Certified Origins can participate.  The number of participants across the network has no limit or impact on performance.</p>
          <p>Blockchain also offers a more general innovation opportunity. As the technology becomes ubiquitous in business processes and across industries, more specialized use cases and value will no doubt emerge.  As pioneers in traceability, Certified Origins and Bellucci are well-positioned to reap the benefits of transformation.</p>
        `,
        type: "default"
      }
    },
    {
      component: "SectionSeperator",
      content: {
        image: null
      }
    },
    {
      component: "SectionReferences",
      content: {
        references: [
          {
            title: "nielson.com",
            source:
              "https://www.nielsen.com/uk/en/press-room/2016/nielsen-brand-origins.html"
          },
          {
            title: "ifcnr.org",
            source:
              "https://ifcnr.org/extra-virgin-olive-oil-fraud-yes-no-maybe/"
          },
          {
            title: "business.com",
            source:
              "https://www.business.com/articles/blockchain-technology-impact-future/"
          }
        ]
      }
    },
    {
      component: "SectionCallToAction",
      content: {
        title: `Transform your business with Oracle`,
        ctas: [
          {
            image: require("../../assets/images/svgs/report.svg"),
            copy: "Decision makers and technology report",
            url:
              "https://www.oracle.com/uk/area-of-interest/it/leading-technologies-survey.html?source=:ow:o:cs:mt:RC_EMMK181024P00070C0001:YTTFY19_IT_AW_HI_CM_UK_C33_Q33_RT&intcmp=:ow:o:cs:mt:RC_EMMK181024P00070C0001:YTTFY19_IT_AW_HI_CM_UK_C33_Q33_RT",
            buttonCopy: "Read report"
          }
        ],
        type: "cta"
      }
    }
  ]
};

export default content;
